import { z } from 'zod';
import { parseDocument } from 'yaml';
import { getNunjucksSyntaxError } from '@agilelab/plugin-wb-platform-common';
import { MarketplaceProjectsFiltersValidator } from '../collators/schema';
import { defaultBusinessConceptDescriptorPaths } from '../businessConceptValues';

const CustomPageParameterZod = z
  .object({
    name: z.string().trim().min(1),
    valuePath: z.string().trim().min(1),
  })
  .strict();

const CustomPageZod = z
  .object({
    title: z.string().trim().min(1),
    url: z.string().trim().url(),
    route: z.string().trim().min(1).regex(/^\//),
    identifier: z
      .string()
      .trim()
      .min(1)
      .regex(/^[a-zA-Z0-9_-]*$/),
    mountFunction: z.string().trim().min(1),
    unmountFunction: z.string().trim().min(1),
    parameters: z.array(CustomPageParameterZod),
  })
  .strict();

const CustomProjectPageConditions = z
  .object({
    useCaseTemplateIds: z.array(z.string().trim().min(1)).optional(),
    types: z.array(z.string().trim().min(1)).optional(),
  })
  .strict();

const ExtendedCustomPage = CustomPageZod.extend({
  conditions: CustomProjectPageConditions.optional(),
}).strict();

const conceptExpressionZod = z.string().superRefine((expression, context) => {
  const error = getNunjucksSyntaxError(expression);
  if (error) context.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

export function getBusinessConceptConfigSyntaxError(
  source: string,
): string | undefined {
  const document = parseDocument(source, {
    uniqueKeys: true,
    prettyErrors: false,
  });
  return document.errors.length
    ? document.errors.map(error => error.message).join('; ')
    : undefined;
}

export const externalConceptExpressionsZod = z
  .object({
    default: conceptExpressionZod.optional(),
    byPrefix: z.record(conceptExpressionZod).optional(),
  })
  .strict();

export const businessConceptsConfigZod = z
  .object({
    descriptorPaths: z.array(z.string()).optional(),
    enforceIri: z.boolean().optional(),
    externalDisplayLabel: externalConceptExpressionsZod.optional(),
    externalUrl: externalConceptExpressionsZod.optional(),
  })
  .strict();

export const businessConceptMapConfigZod = z
  .object({
    enabled: z.boolean().optional(),
    relatedProductsMaxDepth: z.number().finite().optional(),
  })
  .strict();

export const ConfigValidator = z.object({
  businessConcepts: businessConceptsConfigZod.optional(),
  businessConceptMap: businessConceptMapConfigZod.optional(),
  fe: z.object({
    displayNameDescriptorField: z.object({
      default: z.string(),
      types: z.record(z.string()).optional(),
    }),
    customPages: z
      .object({
        page: z.array(CustomPageZod).optional(),
        projectPage: z.array(ExtendedCustomPage).optional(),
        consumableInterfacePage: z.array(ExtendedCustomPage).optional(),
      })
      .strict()
      .optional(),
  }),
  search: z.object({
    'marketplace-projects': z.object({
      filters: MarketplaceProjectsFiltersValidator,
    }),
  }),
  embeddingSearch: z
    .object({
      enabled: z.boolean(),
    })
    .optional(),
});

export type ConfigSchema = z.infer<typeof ConfigValidator>;

export const defaultConfig: DeepPartial<ConfigSchema> = {
  businessConcepts: {
    descriptorPaths: defaultBusinessConceptDescriptorPaths,
    enforceIri: false,
  },
  businessConceptMap: {
    enabled: false,
    relatedProductsMaxDepth: 2,
  },
  fe: {
    displayNameDescriptorField: {
      default: 'name',
    },
  },
};

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};
